/**
 * Email Signup Block Component
 *
 * Handles dynamic padding adjustment for email signup input fields
 * when integrated buttons have variable widths, and validates email format.
 */
class EmailSignupBlock extends HTMLElement {
  constructor() {
    super();
    /** @type {number} */
    this.lastButtonWidth = 0;
    /** @type {ResizeObserver | undefined} */
    this.observer = undefined;

    this.adjustInputPadding = this.adjustInputPadding.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.validateOnInput = this.validateOnInput.bind(this);
    this.validateOnBlur = this.validateOnBlur.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('.email-signup__button--integrated');
    this.input = this.querySelector('.email-signup__input');
    this.inputGroup = this.querySelector('.email-signup__input-group');
    this.form = this.querySelector('form');
    this.emailInput = this.querySelector('input[type="email"]');

    const isTextButton = this.button && this.button.classList.contains('email-signup__button--text');

    // Existing padding adjustment logic
    if (this.button && this.input && this.inputGroup && isTextButton) {
      this.adjustInputPadding();

      this.observer = new ResizeObserver(() => {
        this.adjustInputPadding();
      });
      this.observer.observe(this.button);

      if ('fonts' in document) {
        document.fonts.ready.then(() => {
          this.adjustInputPadding();
        });
      }
    }

    // Email validation logic
    if (this.form && this.emailInput) {
      this.form.addEventListener('submit', this.handleSubmit);
      this.emailInput.addEventListener('input', this.validateOnInput);
      this.emailInput.addEventListener('blur', this.validateOnBlur);
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }

    // Clean up event listeners
    if (this.form && this.emailInput) {
      this.form.removeEventListener('submit', this.handleSubmit);
      this.emailInput.removeEventListener('input', this.validateOnInput);
      this.emailInput.removeEventListener('blur', this.validateOnBlur);
    }
  }

  adjustInputPadding() {
    if (!this.button || !this.inputGroup) return;

    const buttonWidth = /** @type {HTMLElement} */ (this.button).offsetWidth;

    if (buttonWidth !== this.lastButtonWidth) {
      this.lastButtonWidth = buttonWidth;
      /** @type {HTMLElement} */ (this.inputGroup).style.setProperty('--button-actual-width', `${buttonWidth}px`);
    }
  }

  handleSubmit(event) {
    const email = this.emailInput.value.trim();
    
    if (!this.isValidEmail(email)) {
      event.preventDefault();
      event.stopPropagation();
      this.showError('Please enter a valid email address');
      return false;
    }
    
    // Clear any previous custom validity
    this.emailInput.setCustomValidity('');
    return true;
  }

  validateOnInput() {
    const email = this.emailInput.value.trim();
    
    if (email.length > 0 && !this.isValidEmail(email)) {
      this.emailInput.setCustomValidity('Please enter a valid email address');
    } else {
      this.emailInput.setCustomValidity('');
    }
  }

  validateOnBlur() {
    const email = this.emailInput.value.trim();
    
    if (email.length > 0 && !this.isValidEmail(email)) {
      this.showError('Please enter a valid email address');
    }
  }

  isValidEmail(email) {
    // Comprehensive email validation regex
    // Requires: username@domain.extension (extension must be 2+ characters)
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
    
    return emailRegex.test(email);
  }

  showError(message) {
    this.emailInput.setCustomValidity(message);
    this.emailInput.reportValidity();
  }
}

if (!customElements.get('email-signup-block')) {
  customElements.define('email-signup-block', EmailSignupBlock);
}
